<template>
  <div class="vap-page">
    <Loading
      :active.sync="isLoading"
      :is-full-page="fullPage"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-page-header">
      <h1>AWS Role Assumption</h1>
    </div>
    <div class="vap-page-content">
      <div class="aws-role-assumption-form">
        <VasionDropList
          v-slot="slotItem"
          v-model="selectedRegion"
          title="AWS Region"
          :dataArray="awsRegionList"
          width="100%"
          :showSearchField="false"
          type="plain-list"
          displayName="displayName"
          valueName="value"
          class="aws-region-spacing"
        >
          {{ slotItem.item.displayName }}
        </VasionDropList>

        <div class="button-and-download">
          <VasionButton
            id="create-aws-stack-button"
            classProp="primary"
            :isDisabled="!canCreateStack"
            @vasionButtonClicked="openCreateStack"
          >
            Create CloudFormation Stack
          </VasionButton>
          <div>You can preview the template and make any changes to it by clicking the above button.
          </div>
        </div>

        <p v-if="awsRegionsError" class="error-message">
          {{ awsRegionsError }}
        </p>
        <p v-if="externalIdError" class="error-message">
          {{ externalIdError }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import VasionDropList from '@/components/shared/VasionDropList.vue'
import VasionButton from '@/components/shared/VasionButton.vue'

const DEFAULT_REGION = 'us-west-2'
const CLOUDFORMATION_CONSOLE_BASE = 'https://{region}.console.aws.amazon.com/cloudformation/home?region={region}'
const CLOUDFORMATION_HASH = '#/stacks/create/review'
// Template URL is static and does not vary by region
const TEMPLATE_URL = 'https://prod-s3-customerdownloadsbucket-pkudaztrodac.s3.us-east-1.amazonaws.com/templates/aws-integrations-role.yaml'
const STACK_NAME = 'VasionAutomateProStack'

export default {
  name: 'AwsRoleAssumption',
  components: {
    VasionDropList,
    VasionButton,
  },
  data() {
    return {
      awsRegionList: [],
      selectedRegion: null,
      awsRegionsError: '',
      externalId: '',
      externalIdError: '',
      isLoading: false,
    }
  },
  computed: {
    canCreateStack() {
      const hasRegion = this.selectedRegion && this.selectedRegion.value
      const hasExternalId = typeof this.externalId === 'string' && this.externalId.trim() !== ''
      return hasRegion && hasExternalId && !this.externalIdError
    },
    templateDownloadUrl() {
      return TEMPLATE_URL
    },
  },
  async mounted() {
    this.isLoading = true
    await this.getAwsRegions()
    await this.getExternalId()
    this.setDefaultRegion()
    this.isLoading = false
  },
  methods: {
    async getAwsRegions() {
      this.awsRegionsError = ''
      try {
        const response = await this.$store.dispatch('admin/getAwsRegions')
        if (response?.data?.Values) {
          this.awsRegionList = response.data.Values.map(region => ({ value: region, displayName: region }))
        } else {
          this.awsRegionsError = 'The AWS regions could not be retrieved. Please try again later or contact your administrator.'
        }
      } catch (e) {
        console.error('Error retrieving AWS regions:', e)
        this.awsRegionsError = 'The AWS regions could not be retrieved. Please try again later or contact your administrator.'
      }
    },
    async getExternalId() {
      this.externalIdError = ''
      try {
        const response = await this.$store.dispatch('admin/getAwsRoleExternalId')
        const value = response?.data?.Value
        if (value != null && value !== '') {
          this.externalId = value
        } else {
          this.externalIdError = 'The AWS External ID could not be retrieved. Please try again later or contact your administrator.'
        }
      } catch (e) {
        console.error('Error retrieving AWS External ID:', e)
        this.externalIdError = 'The AWS External ID could not be retrieved. Please try again later or contact your administrator.'
      }
    },
    setDefaultRegion() {
      const defaultOption = this.awsRegionList.find(r => r.value === DEFAULT_REGION)
      this.selectedRegion = defaultOption || (this.awsRegionList.length ? this.awsRegionList[0] : null)
    },
    openCreateStack() {
      if (!this.canCreateStack) return
      const region = this.selectedRegion.value
      const params = new URLSearchParams({
        templateURL: TEMPLATE_URL,
        stackName: STACK_NAME,
        param_ExternalId: this.externalId,
      })
      const consoleBase = CLOUDFORMATION_CONSOLE_BASE.replace(/{region}/g, region)
      // AWS puts template params in the hash: #/stacks/create/review?templateURL=...
      const url = `${consoleBase}${CLOUDFORMATION_HASH}?${params.toString()}`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
  },
}
</script>

<style lang="scss" scoped>
.aws-role-assumption-form {
  max-width: 500px;
}

.aws-region-spacing {
  margin-bottom: 20px;
}

.button-and-download {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.download-template-link {
  color: var(--vasion-link-color, #1976d2);
  text-decoration: underline;
}

.download-template-link:hover {
  text-decoration: none;
}

.error-message {
  color: var(--vasion-error-color, #c62828);
  margin-top: 16px;
}
</style>
